import { useMarketplaceLabel, marketplaceExportFields } from "../configurazione/marketplaceDisplay";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import DataTable from "../../interfaccia/DataTableBase";
import umanizzaDataSenzaOre from "../../interfaccia/funzioniComuni/umanizzaDataSenzaOre";

function padTo2Digits(num) {
  return num.toString().padStart(2, "0");
}

function formatDateInput(date = new Date()) {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join("-");
}

function parseDateInput(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function daysInclusive(startDate, endDate) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay) + 1;
}

function normalizeDateForCsv(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

function formatDateForTable(value) {
  if (!value) return "";
  try {
    return umanizzaDataSenzaOre(value);
  } catch (err) {
    return normalizeDateForCsv(value);
  }
}

function escapeCsvValue(value) {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (
    stringValue.includes(";") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function downloadCsvFromRows(rows, fileName) {
  const delimiter = ";";
  const headers = Object.keys(rows[0] || {});
  const lines = [headers.join(delimiter)];

  rows.forEach((row) => {
    const line = headers
      .map((header) => escapeCsvValue(row[header]))
      .join(delimiter);
    lines.push(line);
  });

  const csv = `\uFEFF${lines.join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function flattenRowsByArticle(spedizioniRows = []) {
  if (!Array.isArray(spedizioniRows)) return [];
  return spedizioniRows.flatMap((spedizione) => {
    const articoli = Array.isArray(spedizione.articoliArr)
      ? spedizione.articoliArr
      : [];
    if (!articoli.length) return [];
    return articoli.map((articolo, idx) => ({
      rowKey: `${spedizione.idSpedizioneDomino || "sped"}-${idx}`,
      articoloIndex: idx + 1,
      ...spedizione,
      codiceVsArticolo: articolo.codiceVsArticolo || "",
      descrizioneArticolo: articolo.descrizioneArticolo || "",
      importoUnitario: articolo.importoUnitario ?? "",
      quantita: articolo.quantita ?? "",
      repartoCode: articolo.repartoCode || "",
      repartoDesc: articolo.repartoDesc || "",
      categoriaCode: articolo.categoriaCode || "",
      categoriaDesc: articolo.categoriaDesc || "",
      tipoCode: articolo.tipoCode || "",
      tipoDesc: articolo.tipoDesc || "",
      sottotipoCode: articolo.sottotipoCode || "",
      sottotipoDesc: articolo.sottotipoDesc || "",
      personaCode: articolo.personaCode || "",
      personaDesc: articolo.personaDesc || "",
      categoriaPredefinitaId: articolo.categoriaPredefinitaId ?? "",
      categoriaPredefinitaDesc: articolo.categoriaPredefinitaDesc || "",
      fasciaPrezziSpedizioneValueId:
        articolo.fasciaPrezziSpedizioneValueId ?? "",
      fasciaPrezziSpedizioneDesc: articolo.fasciaPrezziSpedizioneDesc || "",
      metodoSpedizioneValueId: articolo.metodoSpedizioneValueId ?? "",
      metodoSpedizioneDesc: articolo.metodoSpedizioneDesc || "",
      aspettoCalzatureValueId: articolo.aspettoCalzatureValueId ?? "",
      aspettoCalzatureDesc: articolo.aspettoCalzatureDesc || "",
    }));
  });
}

function compareArticleRowsForExport(a, b) {
  const puntoA = String(a?.puntoRitiro || "").trim();
  const puntoB = String(b?.puntoRitiro || "").trim();
  const byPunto = puntoA.localeCompare(puntoB, "it-IT", {
    sensitivity: "base",
  });
  if (byPunto !== 0) return byPunto;

  const dataA = normalizeDateForCsv(a?.dataPartenza || "");
  const dataB = normalizeDateForCsv(b?.dataPartenza || "");
  if (dataA < dataB) return -1;
  if (dataA > dataB) return 1;

  const ordineA = String(a?.numeroOrdinePrestashop || "").trim();
  const ordineB = String(b?.numeroOrdinePrestashop || "").trim();
  const byOrdine = ordineA.localeCompare(ordineB, "it-IT", {
    numeric: true,
    sensitivity: "base",
  });
  if (byOrdine !== 0) return byOrdine;

  const idA = Number(a?.idSpedizioneDomino) || 0;
  const idB = Number(b?.idSpedizioneDomino) || 0;
  if (idA !== idB) return idA - idB;

  return (Number(a?.articoloIndex) || 0) - (Number(b?.articoloIndex) || 0);
}

function StatControlloFattureCorrieri({ token }) {
  const marketplaceLabel = useMarketplaceLabel();
  const today = new Date();
  const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [input, setInput] = useState({
    dataStart: formatDateInput(startMonth),
    dataEnd: formatDateInput(today),
    corriereFiltro: "brt_dpd",
  });
  const [rangeApplicato, setRangeApplicato] = useState({
    dataStart: formatDateInput(startMonth),
    dataEnd: formatDateInput(today),
    corriereFiltro: "brt_dpd",
  });
  const [spedizioniRows, setSpedizioniRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          dataStart: rangeApplicato.dataStart,
          dataEnd: rangeApplicato.dataEnd,
          corriereFiltro: rangeApplicato.corriereFiltro,
        });
        const response = await axios.get(
          `${apiUrl}/contab/get/controlloFattureCorrieri?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSpedizioniRows(response?.data?.data || []);
      } catch (err) {
        console.error("Errore caricamento controllo fatture corrieri", err);
        setError(
          err?.response?.data?.error ||
            "Errore caricamento controllo fatture corrieri",
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    token,
    rangeApplicato.dataStart,
    rangeApplicato.dataEnd,
    rangeApplicato.corriereFiltro,
  ]);

  const columns = useMemo(
    () => [
      {
        selector: (row) => row.dataPartenza,
        name: "Data partenza",
        sortable: true,
        width: "140px",
        format: (row) => formatDateForTable(row.dataPartenza),
      },
      {
        selector: (row) => row.puntoRitiro,
        name: "Punto di ritiro",
        sortable: true,
        grow: 1.1,
      },
      {
        selector: (row) => row.numeroOrdinePrestashop,
        name: "N. ordine prestashop",
        sortable: true,
        width: "170px",
      },
      {
        selector: (row) => row.codiceVsArticolo,
        name: "Codice VS articolo",
        sortable: true,
        width: "180px",
      },
      {
        selector: (row) => row.descrizioneArticolo,
        name: "Descrizione articolo",
        sortable: true,
        grow: 2.2,
      },
      {
        selector: (row) =>
          row.categoriaPredefinitaDesc || row.categoriaPredefinitaId || "",
        name: "WEB Cat web",
        sortable: true,
        grow: 1.6,
      },
      {
        selector: (row) => row.repartoDesc || "",
        name: "MODA Reparto",
        sortable: true,
        grow: 1.4,
      },
      {
        selector: (row) => row.categoriaDesc || "",
        name: "MODA Categoria",
        sortable: true,
        grow: 1.5,
      },
      {
        selector: (row) => row.tipoDesc || "",
        name: "MODA Tipo",
        sortable: true,
        grow: 1.3,
      },
      {
        selector: (row) => row.personaDesc || "",
        name: "MODA Persona",
        sortable: true,
        grow: 1.3,
      },
      {
        selector: (row) => Number(row.quantita) || 0,
        name: "Quantità",
        sortable: true,
        width: "110px",
      },
    ],
    [],
  );

  const articleRows = useMemo(
    () => flattenRowsByArticle(spedizioniRows),
    [spedizioniRows],
  );
  const orderedArticleRows = useMemo(
    () => [...articleRows].sort(compareArticleRowsForExport),
    [articleRows],
  );

  function handleChange(evt) {
    const { name, value } = evt.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  }

  function applicaRange() {
    const start = parseDateInput(input.dataStart);
    const end = parseDateInput(input.dataEnd);
    if (!start || !end) {
      setError("Inserire date valide.");
      return;
    }
    if (start.getTime() > end.getTime()) {
      setError(
        "Il range date non è valido: data iniziale successiva alla finale.",
      );
      return;
    }
    if (daysInclusive(start, end) > 31) {
      setError("Range massimo consentito: 31 giorni.");
      return;
    }
    setError("");
    setRangeApplicato({ ...input });
  }

  function esportaCsv() {
    if (!orderedArticleRows.length) return;

    const csvRows = orderedArticleRows.map((row) => ({
      Corriere: row.corriere || "",
      "Data partenza": normalizeDateForCsv(row.dataPartenza),
      "Punto di ritiro": row.puntoRitiro || "",
      "N. ordine prestashop": row.numeroOrdinePrestashop || "",
      "Rif. ordine marketplace": row.rifOrdineMarketplace || "",
      ...marketplaceExportFields(row.marketplace, marketplaceLabel),
      "Data ordine": normalizeDateForCsv(row.dataOrdine),
      "Totale importo ordine": row.totaleImportoOrdine ?? "",
      "Totale importo contrassegno": row.totaleImportoContrassegno ?? "",
      "Tipo ordine": row.tipoOrdine || "",
      "ID Spedizione Domino": row.idSpedizioneDomino || "",
      "Paese spedizione": row.paeseSpedizione || "",
      NomeCognome: row.nomeCognome || "",
      "C/O": row.careOf || "",
      Indirizzo: row.indirizzo || "",
      CAP: row.cap || "",
      Città: row.citta || "",
      Provincia: row.provincia || "",
      "Progressivo articolo spedizione": row.articoloIndex,
      "Codice VS articolo": row.codiceVsArticolo || "",
      "Descrizione articolo": row.descrizioneArticolo || "",
      "Importo unitario articolo": row.importoUnitario ?? "",
      "Quantità articolo": row.quantita ?? "",
      "Reparto code": row.repartoCode || "",
      "Reparto desc": row.repartoDesc || "",
      "Categoria code": row.categoriaCode || "",
      "Categoria desc": row.categoriaDesc || "",
      "Tipo code": row.tipoCode || "",
      "Tipo desc": row.tipoDesc || "",
      "Sottotipo code": row.sottotipoCode || "",
      "Sottotipo desc": row.sottotipoDesc || "",
      "Persona code": row.personaCode || "",
      "Persona desc": row.personaDesc || "",
      "Categoria web id": row.categoriaPredefinitaId ?? "",
      "Categoria web desc": row.categoriaPredefinitaDesc || "",
      "Fascia spedizione id": row.fasciaPrezziSpedizioneValueId ?? "",
      "Fascia spedizione desc": row.fasciaPrezziSpedizioneDesc || "",
      "Metodo spedizione id": row.metodoSpedizioneValueId ?? "",
      "Metodo spedizione desc": row.metodoSpedizioneDesc || "",
      "Aspetto calzature id": row.aspettoCalzatureValueId ?? "",
      "Aspetto calzature desc": row.aspettoCalzatureDesc || "",
    }));

    const fileName = `controllo-fatture-corrieri-articoli-${rangeApplicato.dataStart}_${rangeApplicato.dataEnd}-${rangeApplicato.corriereFiltro}.csv`;
    downloadCsvFromRows(csvRows, fileName);
  }

  return (
    <div className="sfondoBiancoTema pa3">
      <div className="w-100 flex flex-wrap justify-center items-end tc mb3">
        <div className="w-50 pa2 pb0 mr2 mw5">
          <label className="mr1 b" htmlFor="dataStart">
            Da data
          </label>
          <input
            type="date"
            name="dataStart"
            onChange={handleChange}
            value={input.dataStart}
            className="input-reset inputOrizzontale"
          />
        </div>
        <div className="w-50 pa2 pb0 mr2 mw5">
          <label className="mr1 b" htmlFor="dataEnd">
            a data
          </label>
          <input
            type="date"
            name="dataEnd"
            onChange={handleChange}
            value={input.dataEnd}
            className="input-reset inputOrizzontale"
          />
        </div>
        <div className="w-100 pa2 pb0 ml2 mr2 mw4">
          <label className="mr1 b" htmlFor="corriereFiltro">
            Corriere
          </label>
          <select
            name="corriereFiltro"
            value={input.corriereFiltro}
            onChange={handleChange}
            className="input-reset inputOrizzontale"
          >
            <option value="brt_dpd">BRT/DPD</option>
            <option value="gls">GLS</option>
            <option value="dhl">DHL</option>
            <option value="ups">UPS</option>
            <option value="altro">Altro</option>
          </select>
        </div>
        <button
          onClick={applicaRange}
          className="dim tastoInputOrizzontaleNoRadNoHeight br2 mr3 ml3"
        >
          {stilePulsanti.tastoCercaAspetto}
        </button>
        <button
          onClick={esportaCsv}
          className={stilePulsanti.buttonClassesWhite}
          disabled={loading || orderedArticleRows.length === 0}
        >
          Esporta CSV controllo fatture
        </button>
      </div>

      <div className="gray tc mb3">
        Range applicato (max 31 giorni, su Data_partenza):{" "}
        {rangeApplicato.dataStart} → {rangeApplicato.dataEnd}
        {" | "}Corriere: {rangeApplicato.corriereFiltro}
        {" | "}Spedizioni: {spedizioniRows.length}
        {" | "}Righe articolo: {orderedArticleRows.length}
      </div>

      {error && <div className="dark-red tc mb3">{error}</div>}

      <DataTable
        data={orderedArticleRows}
        columns={columns}
        progressPending={loading}
        noDataComponent={
          <div className="pa4 tc">
            Nessun articolo trovato nel filtro selezionato.
          </div>
        }
        pagination
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        paginationPerPage={10}
        paginationComponentOptions={{
          rowsPerPageText: "",
          rangeSeparatorText: "di",
          noRowsPerPage: false,
          selectAllRowsItem: false,
          selectAllRowsItemText: "Seleziona tutti",
        }}
      />
    </div>
  );
}

export default StatControlloFattureCorrieri;
